import merge from 'lodash/merge';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import omitBy from 'lodash/omitBy';
import isNull from 'lodash/isNull';
import log from 'loglevel';
import promiseRetry from 'promise-retry';
import states from './helpers/states';

// decodeURIComponent before reading a cookie and encodeURIComponent before writing a cookie
import Cookie from 'js-cookie';
const jsCookie =  Cookie.withConverter({
  read: function (value) {
    return decodeURIComponent(value);
  },
  write: function (value) {
    return encodeURIComponent(value);
  }
});

// geolocator
class Geolocator {

  // setup
  constructor() {
    this.promiseRetry = {
      retries: 2, // runs 3 times total
      minTimeout: 2000
    };
    this.skipPromiseRetryForErrors = ['INVALID_COUNTRY', 'PERMISSION_DENIED', 'UNSUPPORTED_BROWSER'];
    this.geoData = {};
    this.status = {
      error: false,
      manualSetInvoked: false
    };
    this.geoDataCookie = {
      name: process.env.brand === 'nissan' ? 'nissanGeolocationData' : 'infinitiGeolocationData',
      value: null,
      options: {
        expires: 90  // days
      }
    };
    this.subscribers = [];
    this.DTM = null;
  }

  /**
   * Function to initialize Geolocation utility on page load. Loads and resolves
   * with cookie data (if available) else kicks off a fetch. Assumes Google Maps
   * already loaded and read to use. Best scenario is that Google actually calls
   * this script internally through:
   * googlemapssrc?key=key&callback=window.CM.NIFTI.GEOLOCATION.geolocator.init
   * @public
   * @return {Promise} resolves with geolocation, rejects with error object
   */
  init() {
    this.DTM = window.CM.NIFTI.DTM;
    return new Promise((resolve, reject) => {
      // check cookie first
      this._manageDataFromGeoCookie();

      // if the geolocation cookie value does not exist run geolocation and store the data in a new geolocation cookie
      if(!this.geoDataCookie.value) {
        log.debug('GEOLOCATION: '+ this.geoDataCookie.name +' cookie value NOT found, geolocator initialized.');
        this.locate().then(resolve).catch(reject);
      } else {
        log.debug('GEOLOCATION: '+ this.geoDataCookie.name +' cookie value found, no need to get geolocation data.');
        resolve(this._returnResults());
        this._trackLocation(1, "cookie-found", "", "cookie", "dtmGeoCookieFound");
      }
    });
  }

  /**
   * Function to locate the current user. Gets their lat,lng and then
   * calls Google Maps to geocode that into an address. Also has retry logic,
   * so if either step fails it will retry a couple times.
   * @public
   * @return {Promise} resolves with location, rejects with error (see this.status.error for template)
   */
  locate() {
    let attempt = 1;
    return new Promise((resolve, reject) => {
      promiseRetry((retry, number) => {
        log.debug(`GEOLOCATION: Attempting to fetch geolocation data... (attempt number ${number})`);
        attempt = number;
        return this._fetchGeolocation().catch((error) => {
          if(this.skipPromiseRetryForErrors.indexOf(error) === -1) {
            retry(error);
          }
          throw error;
        });
      }, this.promiseRetry).then((location) => {
        resolve(location);
        this._trackLocation(attempt);
      }, (error) => {
        reject(error);
        this._trackLocation(attempt, "fail", error, "geo");
        if(error === 'INVALID_COUNTRY') {
          this.geoData = {}; // wipe geodata object
        }
      });
    });
  }

  /**
   * Function to subscribe to geolocation changes. Each time the this.geoData object
   * mutates, all subscribers callbacks will be executed.
   * @public
   * @param {Function} callback Function to execute. Function will be called with two values, newValue and oldValue.
   */
  subscribe(cb) {
    this.subscribers.push(cb);
  }

  /**
   * Function to set geolocation object programmatically. Updates geodata (memory
   * and cookie) and notifies subscribers.
   * @public
   * @param {Object|String} key The key to update (if string). If Object is passed will merge with geodata object.
   * @param {String} value The value to update (if key was passed as string)
   */
  set(key, value) {
    this.status.manualSetInvoked = true;
    let oldResults = cloneDeep(this._returnResults());

    if(typeof key === 'object' && !value){
      if(key.state_abbreviated && !key.state) {
        key.state = states[key.state_abbreviated] || '';
      } else if(key.state_abbreviated === null || key.state === null) {
        key.state_abbreviated = null; // if they pass in null for either state or state_abbreviated set them both to null (aka remove from cookie)
        key.state = null;
      }
      merge(this.geoData, key);
      this.geoData = omitBy(this.geoData, isNull); // remove any null key/value pairs
      this._trackLocation("", "user-input", "", "user", "dtmGeoUser");
    } else{
      set(this.geoData, key, value);
    }

    // update cookie and sync geo data
    this._setGeoCookie();
    this._publish(this._returnResults(), oldResults);
  }

  /**
   * Function to destroy instance of service. Also clears cookie variables.
   * @public
   */
  destroy() {
    jsCookie.remove(this.geoDataCookie.name);
    this.subscribers = [];
    this.geoData = {};
    this.status = {
      error: false,
      manualSetInvoked: false
    };
    this.geoDataCookie.value = null;
  }

  /**
   * Function to fetch geolocation. Handles error handling, debugging, and setting cookie
   * values.
   * @private
   * @return {Promise} Resolves with current location, rejects with error object.
   */
  _fetchGeolocation() {
    return new Promise((resolve, reject) => {
      this._getLatLng()
        .then((lngLat) => {
          // success
          log.debug('GEOLOCATION: Longitude/Latitude promise success: ', lngLat);
          return Promise.all([lngLat, this._getLocationFromLatLng(lngLat)]);
        })
        .then((geoData) => {
          // success
          log.debug('GEOLOCATION: Reverse geocoding promise success: ', geoData);
          // setup
          this.status = {
            error: false,
            manualSetInvoked: false
          };
          this.geoData = geoData[1];
          // create cookie and store geolocation data
          this._setGeoCookie();
          // resolve
          resolve(this._returnResults());
        })
        .catch((error) => {
          // error
          this.status.error = error;
          // log and reject
          log.debug('GEOLOCATION: Error: ', error);
          reject(this.status.error);
        });
    });
  }

  /**
   *  Function that returns objects as one result
   *  @private
   */
  _returnResults(){
    return {geoData: this.geoData, status: this.status};
  }

  /**
   *  Function to sort the data from the cookie's value
   *  @private
   */
  _manageDataFromGeoCookie() {
    // get value from cookie
    let cookieValue = jsCookie.getJSON(this.geoDataCookie.name);

    if(cookieValue){
      // sync data
      this.geoDataCookie.value = JSON.stringify(cookieValue);
      // take objects out of the cookie's value and merge with our objects
      merge(this.status, cookieValue.status);
      merge(this.geoData, cookieValue.geoData);
    } else{
      // no cookie found
      this.geoDataCookie.value = null;
    }
  }

  /**
   * Function to fetch lat lng from navigator.geolocation (HTML5 tag). Supports
   * error handling.
   * @private
   * @return {Promise} resolves with {lat, lng} rejects with error message.
   */
  _getLatLng() {
    return new Promise((resolve, reject) => {
      if(navigator && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.geoData.longitude = position.coords.longitude;
          this.geoData.latitude = position.coords.latitude;
          resolve({
            'lng': position.coords.longitude,
            'lat': position.coords.latitude
          });
        },
        function(error){
          let rejectMsg = "UNKNOWN_ERROR";
          switch(error.code) {
            case error.PERMISSION_DENIED:
              rejectMsg = "PERMISSION_DENIED";
              break;
            case error.POSITION_UNAVAILABLE:
              rejectMsg = "POSITION_UNAVAILABLE";
              break;
            case error.TIMEOUT:
              rejectMsg = "TIMEOUT";
              break;
          }
          reject(rejectMsg);
        });
      }
      else {
        reject('UNSUPPORTED_BROWSER');
      }
    });
  }

  /**
   *  Function to get the location of a user using their lat lng. Depends heavily
   *  on Google Maps Geocoder (https://developers.google.com/maps/documentation/geocoding/intro)
   *  @private
   *  @param {Object} {lat, lng} object
   *  @return {Promise} Location object or error message
   */
  _getLocationFromLatLng(lngLat) {
    let geocoder = new google.maps.Geocoder();
    // promise reverse geocoding data
    return new Promise((resolve, reject) => {
      // pass longitude and latitude along to google maps
      geocoder.geocode({'location': lngLat}, (results, status) => {
        if (status === 'OK') {
          if (results[0]) {
            results[0].address_components.forEach((component) => {
              switch (component.types[0]) {
                case 'postal_code':
                  this.geoData.zipCode = component.long_name;
                  break;
                case 'locality':
                  this.geoData.city = component.long_name;
                  break;
                case 'administrative_area_level_1':
                  this.geoData.state_abbreviated = component.short_name;
                  this.geoData.state = component.long_name;
                  break;
                case 'country':
                  this.geoData.country_abbreviated = component.short_name;
                  this.geoData.country = component.long_name;
                  break;
                default:
                  //log.debug('GEOLOCATION: Not setting data for ' + component.types[0] + '.');
              }
            });
            // Handle user not inside USA.
            if(this.geoData.country_abbreviated.toUpperCase() !== 'US') {
              reject('INVALID_COUNTRY');
            } else {
              resolve(this.geoData);
            }
          } else {
            reject('NO_RESULTS');
          }
        } else {
          // Potential errors: https://developers.google.com/maps/documentation/geocoding/intro#StatusCodes
          reject(status);
        }
      });
    });
  }

  /**
   * Function to update cookie from geodata object
   * @private
   */
  _setGeoCookie() {
    // copy of geoData
    let cookieValue = cloneDeep(this._returnResults());

    // omit error if false
    if(!this.status.error) delete cookieValue.status.error;

    // set cookie
    jsCookie.set(this.geoDataCookie.name, cookieValue, { expires: this.geoDataCookie.options.expires });
    log.debug('GEOLOCATION: '+ this.geoDataCookie.name +' cookie created/updated and set with the following geolocation data: ', cookieValue);

    // sync the cooke value
    this.geoDataCookie.value = JSON.stringify(cookieValue);
  }

  /**
   * Function to notify subscribers of geolocation object change.
   * Does not check for dirty calls.
   * @private
   */
  _publish(newValue, oldValue) {
    this.subscribers.forEach((subscription) => {
      subscription(newValue, oldValue);
    });
  }

  /**
   * Internal function for talking with the data layer.
   * @param attempt - number of attempts
   * @param status - "success", "fail", "cookie-found"
   * @param error - error msg
   * @param source - appended to the geodata result object
   * @param tracking - the event to trigger
   */
  _trackLocation(attempt = 1, status = "success", error = "", source = "geo", tracking = 'dtmGeoService') {
    if(this.DTM) {
      let results = cloneDeep(this.geoData);
      results.source = source;
      this.DTM.NnaDtmTracking().directCall('event.geolocation', {status, error, attempt, results}, tracking);
    }
  }
}

export default Geolocator;


// WEBPACK FOOTER //
// ./src/scripts/geolocator.js